/*
  Sticky add to cart.

  Reveals a fixed bar once the real add-to-cart button has scrolled out of
  view, and hides it again when the button comes back. The bar's button is
  not a second form: it targets the existing product form by id, so variant
  selection, sold-out state and every bit of Dawn's cart logic still apply.

  If IntersectionObserver is missing the bar simply never shows, which leaves
  the page exactly as it was.
*/
class PdpStickyAtc extends HTMLElement {
  connectedCallback() {
    // Dawn's scroll-reveal animation puts a transform on an ancestor section,
    // and a transformed ancestor becomes the containing block for fixed
    // children, so the bar would float mid-page instead of pinning to the
    // viewport. Reparenting to <body> escapes that. The button keeps working
    // because it targets the product form by id, not by nesting.
    if (this.parentElement !== document.body) {
      document.querySelectorAll('body > pdp-sticky-atc').forEach((el) => {
        if (el !== this) el.remove();
      });
      document.body.appendChild(this);
      return; // connectedCallback runs again after the move
    }

    this.target = document.querySelector(this.dataset.watch);
    this.button = this.querySelector('[data-sticky-submit]');

    if (this.button) this.syncDisabledState();

    if (!this.target || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only reveal once the button is above the viewport, so the bar
          // never covers content the shopper has not reached yet.
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', scrolledPast);
        });
      },
      { rootMargin: '0px', threshold: 0 }
    );

    this.observer.observe(this.target);

    // Dawn re-renders the buy buttons on variant change, so mirror the
    // sold-out state rather than letting the bar offer an unbuyable variant.
    this.mutationObserver = new MutationObserver(() => this.syncDisabledState());
    this.mutationObserver.observe(this.target, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  syncDisabledState() {
    if (!this.target || !this.button) return;
    const soldOut = this.target.hasAttribute('disabled');
    this.button.disabled = soldOut;

    const label = this.querySelector('[data-sticky-label]');
    if (!label) return;
    const soldOutText = this.dataset.soldOutText || 'Sold out';
    const addText = this.dataset.addText || 'Add to cart';
    label.textContent = soldOut ? soldOutText : addText;
  }
}

if (!customElements.get('pdp-sticky-atc')) {
  customElements.define('pdp-sticky-atc', PdpStickyAtc);
}
